// Auth helpers — talks to /api/auth/* using session_token cookie + Bearer fallback.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthCtx = createContext({ user: null, loading: true, login: () => {}, logout: async () => {} });

export const useAuth = () => useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      if (!r.ok) throw new Error("not authed");
      const data = await r.json();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip the /me check.
    // AuthCallback exchanges the session_id and establishes the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/leads";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      // no-op
    }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh: checkAuth }}>
      {children}
    </AuthCtx.Provider>
  );
};

// Handles the session_id fragment that lands after Google OAuth.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/admin/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || "Sign-in failed");
        }
        const user = await r.json();
        if (refresh) await refresh();
        navigate("/admin/leads", { replace: true, state: { user } });
      } catch (err) {
        setError(err.message || "Sign-in failed");
        setTimeout(() => navigate("/admin/login", { replace: true }), 2000);
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div
      style={{
        minHeight: "100svh",
        display: "grid",
        placeItems: "center",
        background: "var(--ink)",
        color: "var(--paper)",
        fontFamily: "var(--mono)",
        fontSize: ".82rem",
        letterSpacing: ".1em",
        textTransform: "uppercase",
      }}
      data-testid="auth-callback"
    >
      {error || "Signing you in…"}
    </div>
  );
};

export const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/admin/login", { replace: true });
    } else if (adminOnly && !user.is_admin) {
      // Stays on page but renders the access-denied screen below.
    }
  }, [user, loading, adminOnly, navigate]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100svh",
          display: "grid",
          placeItems: "center",
          background: "var(--paper)",
          color: "var(--ink-soft)",
          fontFamily: "var(--mono)",
          fontSize: ".75rem",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
        data-testid="auth-loading"
      >
        Checking session…
      </div>
    );
  }
  if (!user) return null;
  if (adminOnly && !user.is_admin) {
    return (
      <div
        style={{
          minHeight: "100svh",
          display: "grid",
          placeItems: "center",
          background: "var(--paper)",
          color: "var(--ink)",
          padding: "2rem",
          textAlign: "center",
        }}
        data-testid="access-denied"
      >
        <div>
          <p className="mono" style={{ color: "var(--brass-deep)" }}>403 · Access denied</p>
          <h1 className="display" style={{ fontSize: "2.4rem", margin: "1rem 0 .8rem" }}>
            This area is reserved for Asia Sourcing.
          </h1>
          <p style={{ color: "var(--ink-soft)", maxWidth: "40ch", margin: "0 auto" }}>
            You&apos;re signed in as <b>{user.email}</b>, which isn&apos;t the admin account.
          </p>
        </div>
      </div>
    );
  }
  return children;
};
